import Card from "@/UI/Card";
import Loader from "@/components/Loader/Loader";
import {
  deleteAgent,
  loadSingleAgent,
  updateAgent,
  changeAgentStatus,
} from "@/redux/rtk/features/CRM/agent/agentSlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import usePermissions from "@/utils/usePermissions";
import { Button, Form, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFillPencilFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import ImageUploadCrm from "../UI/ImageUploadCrm";

export default function CompanyProfile({ data, loading }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [triggerSave, setTriggerSave] = useState(false);
  const { list: ownerList, loading: ownerLoading } = useSelector(
    (state) => state.users
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onDelete = async () => {
    var result = window.confirm(t("company.deleteAgentConfirm"));
    if (result) {
      const resp = await dispatch(deleteAgent({ id }));
      if (resp.payload.message === "success") {
        navigate(-1);
      }
    }
  };

  const { permissions } = usePermissions();
  const canEdit = permissions?.includes("update-company");

  const onFinish = async (values) => {
    const formData = {
      ...values,
      companyOwnerId: parseInt(values.companyOwnerId),
    };
    const resp = await dispatch(updateAgent({ id: id, values: formData }));
    if (resp.payload.message === "success") {
      dispatch(loadSingleAgent(id));
      setTriggerSave(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setTriggerSave(false);
    form.resetFields();
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        companyOwnerId: data.companyOwnerId,
        firstName: data.firstName,
        lastName: data.lastName,
        website: data.website,
        phone: data.phone,
        email: data.email,
      });
    }
  }, [data, form]);

  if (!data) return <Loader />;

  return (
    <Card className="h-fit lg:sticky lg:top-6 p-3 sm:p-4 md:p-6 border-none w-full">
      <Form
        form={form}
        colon={false}
        disabled={!canEdit}
        layout="vertical"
        onFieldsChange={() => setTriggerSave(true)}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          companyOwnerId: data.companyOwnerId,
          firstName: data?.firstName,
          lastName: data?.lastName,
          website: data?.website,
          phone: data?.phone,
          email: data?.email,
        }}>
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row flex-wrap justify-between items-start pb-4 sm:pb-6 border-b dark:border-gray-700 gap-4">
          <div className="flex flex-col items-start w-full sm:w-auto flex-grow min-w-0">
            <div className="flex flex-row items-center gap-3 sm:gap-4 w-full">
              <div className="flex-shrink-0">
                <ImageUploadCrm
                  data={data}
                  updateThunk={updateAgent}
                  loadThunk={loadSingleAgent}
                />
              </div>

              <div className="flex-1 min-w-0 overflow-hidden">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
                  {data?.firstName} {data?.lastName}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-normal ${data?.isActive !== "false" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {data?.isActive !== "false" ? t("common.active") || "Active" : t("common.inactive") || "Inactive"}
                  </span>
                </h2>
                {data.website && (
                  <Link
                    target="_blank"
                    to={"https://" + data.website}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mt-1 block truncate">
                    {data.website}
                  </Link>
                )}
                {data.phone && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                    {data.phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto self-end sm:self-start">
            {triggerSave ? (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="small"
                  className="flex-1 sm:flex-none">
                  {t("company.saveChanges")}
                </Button>
                <Button
                  loading={loading}
                  onClick={onFinishFailed}
                  size="small"
                  className="flex-1 sm:flex-none">
                  {t("common.cancel")}
                </Button>
              </>
            ) : (
              <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto self-end sm:self-start">
                {permissions?.includes("status-agent") && (
                  <Button
                    type={data?.isActive !== "false" ? "danger" : "primary"}
                    onClick={async () => {
                      const active = data?.isActive !== "false";
                      const action = active ? "deactivate" : "activate";
                      const confirmMsg = t("common.areYouSure", { action: t(`common.${action}`) || action });
                      if (window.confirm(confirmMsg)) {
                        const nextVal = active ? "false" : "true";
                        await dispatch(changeAgentStatus({ id, isActive: nextVal }));
                        dispatch(loadSingleAgent(id));
                      }
                    }}
                    size="small"
                    className="w-full sm:w-auto min-w-[100px]">
                    {data?.isActive !== "false" ? t("common.deactivate") || "Deactivate" : t("common.activate") || "Activate"}
                  </Button>
                )}
                <Button
                  danger
                  onClick={onDelete}
                  size="small"
                  className="w-full sm:w-auto min-w-[100px]">
                  {t("company.deleteAgent")}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 sm:pt-6 space-y-3 sm:space-y-4">
          <Form.Item
            label={t("company.owner")}
            name="companyOwnerId"
            className="mb-3 sm:mb-4">
            <Select
              bordered={false}
              loading={ownerLoading}
              size="large"
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500 w-full">
              {ownerList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item?.firstName} {item?.lastName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("company.firstName", "First Name")}
            name="firstName"
            className="mb-3 sm:mb-4">
            <Input
              bordered={false}
              size="large"
              suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
            />
          </Form.Item>

          <Form.Item
            label={t("company.lastName", "Last Name")}
            name="lastName"
            className="mb-3 sm:mb-4">
            <Input
              bordered={false}
              size="large"
              suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
            />
          </Form.Item>

          <Form.Item label={t("company.website")} name="website" className="mb-3 sm:mb-4">
            <Input
              bordered={false}
              size="large"
              suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
            />
          </Form.Item>

          <Form.Item
            label={t("common.email")}
            name="email"
            className="mb-3 sm:mb-4"
            rules={[
              {
                required: true,
                message: t("company.emailRequired", "Email is required"),
              },
              {
                type: "email",
                message: t("common.invalidEmail", "Please enter a valid email address"),
              },
            ]}>
            <Input
              bordered={false}
              size="large"
              suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
            />
          </Form.Item>

          <Form.Item label={t("company.phoneNumber")} name="phone" className="mb-3 sm:mb-4">
            <Input
              bordered={false}
              size="large"
              suffix={<BsFillPencilFill className="text-gray-400 text-xs" />}
              className="border-b border-gray-200 dark:border-gray-700 rounded-none hover:border-blue-500"
            />
          </Form.Item>
        </div>
      </Form>
    </Card>
  );
}
